import { Container, Sprite, Vector2 } from '../../odie';

import { SizeType } from '../../lib/defs/types';
import { Graph } from '../../lib/pattern/Graph';
import { numberClamp } from '../../lib/util/mathTools';
import type { Cell, MapComponent } from '../components/MapComponent';
import {
  BlockId,
  blockPropsMap,
  DirectionId,
  PowerBlockType,
  powerBlockTypes,
} from '../defs/block';
import { config } from '../defs/config';
import type { BlockEntity } from '../entities/BlockEntity';
import { GameScene } from '../GameScene';
import { AndroidBlockView } from '../views/blocks/AndroidBlockView';
import { AnimalBlockView } from '../views/blocks/AnimalBlockView';
import { BasicBlockView } from '../views/blocks/BasicBlockView';
import { BombBlockView } from '../views/blocks/BombBlockView';
import { CageBlockView } from '../views/blocks/CageBlockView';
import { CandleBlockView } from '../views/blocks/CandleBlockView';
import { ChainBlockView } from '../views/blocks/ChainBlockView';
import { ChameleonBlockView } from '../views/blocks/ChameleonBlockView';
import { CoffinBlockView } from '../views/blocks/CoffinBlockView';
import { CubeBlockView } from '../views/blocks/CubeBlockView';
import { DogBlockView } from '../views/blocks/DogBlockView';
import { DoorBlockView } from '../views/blocks/DoorBlockView';
import { FireboxBlockView } from '../views/blocks/FireboxBlockView';
import { FuguBlockView } from '../views/blocks/FuguBlockView';
import { HatBlockView } from '../views/blocks/HatBlockView';
import { HazardBlockView } from '../views/blocks/HazardBlockView';
import { KappaBlockView } from '../views/blocks/KappaBlockView';
import { KettleBlockView } from '../views/blocks/KettleBlockView';
import { LockBlockView } from '../views/blocks/LockBlockView';
import { MiniFuguBlockView } from '../views/blocks/MiniFuguBlockView';
import { PantherBlockView } from '../views/blocks/PantherBlockView';
import { PipeBlockView } from '../views/blocks/PipeBlockView';
import { NestBlockView } from '../views/blocks/NestBlockView';
import { RocketBlockView } from '../views/blocks/RocketBlockView';
import { SirenBlockView } from '../views/blocks/SirenBlockView';
import { SkullBlockView } from '../views/blocks/SkullBlockView';
import { SlimeBlockView } from '../views/blocks/SlimeBlockView';
import { SpiderBlockView } from '../views/blocks/SpiderBlockView';
import { StatueBlockView } from '../views/blocks/StatueBlockView';
import { ThrowerBlockView } from '../views/blocks/ThrowerBlockView';
import { VirusBlockView } from '../views/blocks/VirusBlockView';
import { ZombieBlockView } from '../views/blocks/ZombieBlockView';
import { FullIceBlockView } from '../views/blocks/FullIceBlockView';
import { CurtainBlockView } from '../views/blocks/CurtainBlockView';
import { HalfIceBlockView } from '../views/blocks/HalfIceBlockView';
import { ThickIceBlockView } from '../views/blocks/ThickIceBlockView';
import { WormBlockView } from '../views/blocks/WormBlockView';
import { CocoonBlockView } from '../views/blocks/CocoonBlockView';
import { ButterflyBlockView } from '../views/blocks/ButterflyBlockView';
import { PondBlockView } from '../views/blocks/PondBlockView';
import { CandyBagBlockView } from '../views/blocks/CandyBagBlockView';
import { CandyBlockView } from '../views/blocks/CandyBlockView';
import { ShellBlockView } from '../views/blocks/ShellBlockView';
import { assertNever } from 'src/replicant/utils';
import { SodaBlockView } from '../views/blocks/SodaBlockView';
import { MushroomBlockView } from '../views/blocks/MushroomBlockView';
import { ChocolateEggBlockView } from '../views/blocks/ChocolateEggBlockView';
import { AlarmBlockView } from '../views/blocks/AlarmBlockView';
import { BubblesBlockView } from '../views/blocks/BubblesBlockView';
import { getMatch2AssetPath } from './assets';
import { ParcelsBlockView } from '../views/blocks/ParcelsBlockView';
import { FossilBlockView } from '../views/blocks/FossilBlockView';
import { CornBlockView } from '../views/blocks/CornBlockView';
import { PopcornBlockView } from '../views/blocks/PopcornBlockView';
import { TunaCanBlockView } from '../views/blocks/TunaCanBlockView';
import { TunaFishBlockView } from '../views/blocks/TunaFishBlockView';
import { RainbowBubbleBlockView } from '../views/blocks/RainbowBubbleBlockView';
import { FirecrackerBoxBlockView } from '../views/blocks/FirecrackerBoxBlockView';
import { CoolerBlockView } from '../views/blocks/CoolerBlockView';
import { TvBlockView } from '../views/blocks/TvBlockView';
import { ShieldBubbleBlockView } from '../views/blocks/ShieldBubbleBlockView';
import { BathtubBlockView } from '../views/blocks/BathtubBlockView';
import { SharpenerBlockView } from '../blocks/sharpener/SharpenerBlockView';
import { BarrelBlockView } from '../blocks/barrel/BarrelBlockView';
import { RainbowSoapBlockView } from '../blocks/rainbowSoap/RainbowSoapBlockView';
import { BigEggBlockView } from '../blocks/bigEgg/BigEggBlockView';
import { PiggyBankBlockView } from '../blocks/piggyBank/PiggyBankBlockView';
import { BalloonBoxBlockView } from '../blocks/balloonBox/BalloonBoxBlockView';
import { OctopusBlockView } from '../blocks/octopus/OctopusBlockView';
import { FlowerFidgetBlockView } from '../blocks/flowerFidget/FlowerFidgetBlockView';
import { GrassBlockView } from '../blocks/grass/GrassBlockView';
import { MagicBunniesBlockView } from '../blocks/magicBunnies/MagicBunniesBlockView';
import features from 'src/replicant/ruleset/features';
import { user } from 'src/StateObserver';
import { DogHouseBlockView } from '../views/blocks/DogHouseBlockView';
import { CrateBlockView } from '../blocks/crate/CrateBlockView';
import { LaserBoxBlockView } from '../blocks/laserBox/LaserBoxBlockView';

// types
//-----------------------------------------------------------------------------
export type BlockIterator = (
  base: BlockEntity,
  overlay?: BlockEntity,
) => boolean | void;

// info
//-----------------------------------------------------------------------------
export function blockIsFrozen(scene: GameScene, entity: BlockEntity) {
  const overlayEntity = blockGetCell(scene, entity)?.overlay?.entity;

  // frozen if same cell has overlay entity with freeze property
  return (
    overlayEntity &&
    entity !== overlayEntity &&
    overlayEntity.c.block.props.freeze
  );
}

export function blockIsBinded(scene: GameScene, entity: BlockEntity) {
  const overlayEntity = blockGetCell(scene, entity)?.overlay?.entity;

  // binded if same cell has overlay entity with bind property
  return (
    overlayEntity &&
    entity !== overlayEntity &&
    overlayEntity.c.block.props.bind
  );
}

export function cellHasBasicAndNoOverlay(cell: Cell) {
  return (
    // must have a base entity
    cell.base &&
    // that is a basic block
    cell.base.entity.c.block.blockId.includes('basic') &&
    // and must not have an overlay entity
    !cell.overlay
  );
}

export function blockGetCell(
  scene: GameScene,
  entity: BlockEntity,
): Cell | undefined {
  return scene.sessionEntity.c.map.getCellAt(entity.c.position.mapPosition);
}

// view
//-----------------------------------------------------------------------------
export function blockUpdateDirection(view: Container, direction: DirectionId) {
  // update sprite orientation
  switch (direction) {
    case 'left':
      view.scaleX = -1;
      break;
    case 'right':
      view.scaleY = 1;
      break;
    case 'up':
      view.rotation = Math.PI / 2;
      break;
    case 'down':
      view.rotation = -Math.PI / 2;
      break;
  }
}

export function blockPositionView(view: Container | Sprite, size: SizeType) {
  // pivot
  view.pivotX = view.width / 2;
  view.pivotY = view.height / 2;

  // position
  view.x = ((size.width || 1) * config.tile.size) / 2;
  view.y = ((size.height || 1) * config.tile.size) / 2;
}

//TODO: review and consolidate
export function blockPositionView2(view: Container, size: SizeType) {
  view.x = ((size.width || 1) * config.tile.size) / 2;
  view.y = ((size.height || 1) * config.tile.size) / 2;
}

export function blockViewAssets(id: BlockId) {
  const props = blockPropsMap[id];
  let assets;
  switch (props.type) {
    case 'android':
      assets = AndroidBlockView.assets();
      break;
    case 'animal':
      assets = AnimalBlockView.assets();
      break;
    case 'basic':
      assets = BasicBlockView.assets(props);
      break;
    case 'bomb':
      assets = [...BombBlockView.assets()];
      break;
    case 'cage':
      assets = CageBlockView.assets();
      break;
    case 'candle':
      assets = CandleBlockView.assets();
      break;
    case 'chain':
      assets = ChainBlockView.assets();
      break;
    case 'chameleon':
      assets = ChameleonBlockView.assets();
      break;
    case 'coffin':
      assets = CoffinBlockView.assets();
      break;
    case 'cube':
      assets = [...CubeBlockView.assets(props)];
      break;
    case 'dog':
      assets = DogBlockView.assets();
      break;
    case 'door': {
      // If new first 2 obstacle visuals is enabled, return dog house block view assets instead
      if (features(user()).newFirst2ObstacleVisuals) {
        assets = DogHouseBlockView.assets();
        break;
      }

      assets = DoorBlockView.assets(props);
      break;
    }
    case 'firebox':
      assets = FireboxBlockView.assets();
      break;
    case 'fugu':
      assets = FuguBlockView.assets();
      break;
    case 'hat': {
      // If new first 2 obstacle visuals is enabled, return crate block view assets instead
      if (features(user()).newFirst2ObstacleVisuals && props.color === 'all') {
        assets = CrateBlockView.assets();
        break;
      }

      assets = HatBlockView.assets(props);
      break;
    }
    case 'hazard':
      assets = HazardBlockView.assets();
      break;
    case 'kappa':
      assets = KappaBlockView.assets();
      break;
    case 'kettle':
      assets = KettleBlockView.assets();
      break;
    case 'lock':
      assets = LockBlockView.assets();
      break;
    case 'miniFugu':
      assets = MiniFuguBlockView.assets();
      break;
    case 'panther':
      assets = PantherBlockView.assets(props);
      break;
    case 'pipe':
      assets = PipeBlockView.assets();
      break;
    case 'rocket':
      assets = [...RocketBlockView.assets()];
      break;
    case 'siren':
      assets = SirenBlockView.assets();
      break;
    case 'skull':
      assets = SkullBlockView.assets();
      break;
    case 'slime':
      assets = SlimeBlockView.assets();
      break;
    case 'spider':
      assets = SpiderBlockView.assets();
      break;
    case 'statue':
      assets = StatueBlockView.assets();
      break;
    case 'thrower':
      assets = ThrowerBlockView.assets();
      break;
    case 'virus':
      assets = VirusBlockView.assets();
      break;
    case 'zombie':
      assets = ZombieBlockView.assets();
      break;
    case 'ice':
      assets = FullIceBlockView.assets();
      break;
    case 'thickIce':
      assets = ThickIceBlockView.assets();
      break;
    case 'fullIce':
      assets = FullIceBlockView.assets();
      break;
    case 'halfIce':
      assets = HalfIceBlockView.assets();
      break;
    case 'curtain':
      assets = CurtainBlockView.assets();
      break;
    case 'worm':
      assets = WormBlockView.assets();
      break;
    case 'cocoon':
      assets = CocoonBlockView.assets();
      break;
    case 'butterfly':
      assets = ButterflyBlockView.assets();
      break;
    case 'pond':
      assets = PondBlockView.assets();
      break;
    case 'candyBag':
      assets = CandyBagBlockView.assets();
      break;
    case 'candy':
      assets = CandyBlockView.assets();
      break;
    case 'shell':
      assets = ShellBlockView.assets();
      break;
    case 'soda':
      assets = SodaBlockView.assets();
      break;
    case 'bubbles':
      assets = BubblesBlockView.assets();
      break;
    case 'alarm':
      assets = AlarmBlockView.assets();
      break;
    case 'chocolateEgg':
      assets = ChocolateEggBlockView.assets();
      break;
    case 'nest':
      assets = NestBlockView.assets();
      break;
    case 'mushroom':
      assets = MushroomBlockView.assets();
      break;
    case 'parcels':
      assets = ParcelsBlockView.assets();
      break;
    case 'fossil':
      assets = FossilBlockView.assets();
      break;
    case 'corn':
      assets = CornBlockView.assets();
      break;
    case 'popcorn':
      assets = PopcornBlockView.assets();
      break;
    case 'tunaCan':
      assets = TunaCanBlockView.assets();
      break;
    case 'tunaFish':
      assets = TunaFishBlockView.assets();
      break;
    case 'rainbowBubble':
      assets = RainbowBubbleBlockView.assets();
      break;
    case 'firecrackerBox':
      assets = FirecrackerBoxBlockView.assets();
      break;
    case 'cooler':
      assets = CoolerBlockView.assets();
      break;
    case 'tv':
      assets = TvBlockView.assets(props);
      break;
    case 'shieldBubble':
      assets = ShieldBubbleBlockView.assets();
      break;
    case 'bathtub':
      assets = BathtubBlockView.assets();
      break;
    case 'wall':
    case 'randomSpawn':
      return [];
    case 'sharpener':
      assets = SharpenerBlockView.assets();
      break;
    case 'barrel':
      assets = BarrelBlockView.assets();
      break;
    case 'rainbowSoap':
      assets = RainbowSoapBlockView.assets();
      break;
    case 'bigEgg':
      assets = BigEggBlockView.assets();
      break;
    case 'piggyBank':
      assets = PiggyBankBlockView.assets();
      break;
    case 'balloonBox':
      assets = BalloonBoxBlockView.assets();
      break;
    case 'octopus':
      assets = OctopusBlockView.assets();
      break;
    case 'flowerFidget':
      assets = FlowerFidgetBlockView.assets();
      break;
    case 'grass':
      assets = GrassBlockView.assets();
      break;
    case 'magicBunnies':
      assets = MagicBunniesBlockView.assets();
      break;
    case 'laserBox':
      assets = LaserBoxBlockView.assets();
      break;
    default:
      assertNever(props);
  }

  return assets.map((asset) =>
    typeof asset === 'string' ? getMatch2AssetPath(asset) : asset,
  );
}

// options
//-----------------------------------------------------------------------------
//TODO: refactor, this way too funky
export function blockOptionFromWall(
  count: number,
  width: number,
  height: number,
): number {
  return count * 100 + width * 10 + height;
}

export function blockOptionToWall(option = 0): {
  count: number;
  size: SizeType;
} {
  const { count, size } = config.blocks.wall;
  return {
    count: numberClamp(Math.floor(option / 100), count.min, count.max),
    size: {
      width: numberClamp(Math.floor(option / 10) % 10, size.min, size.max),
      height: numberClamp(option % 10, size.min, size.max),
    },
  };
}

// iteration
//-----------------------------------------------------------------------------
export function blockIterateAll(
  scene: GameScene,
  handler: BlockIterator,
): boolean {
  const map = scene.sessionEntity.c.map;

  // for each cell in map
  for (let column = 0; column < map.columns; ++column) {
    for (let row = 0; row < map.rows; ++row) {
      const cell = map.grid[column][row];

      // get base block entity
      const entity = cell.base?.entity;

      // if exists, handle it. end if handler succeeds.
      if (entity && handler(entity, cell.overlay?.entity)) return true;
    }
  }

  return false;
}

export function blockIterateNeighbors(
  map: MapComponent,
  startEntity: BlockEntity,
  handler: BlockIterator,
) {
  const visited: Cell[] = [];

  // recurse on given block entity
  _blockIterateNeighborsR(
    map,
    handler,
    visited,
    startEntity.c.position.mapPosition,
  );
}

function _blockIterateNeighborsR(
  map: MapComponent,
  handler: BlockIterator,
  visited: Cell[],
  position: Vector2,
) {
  // get cell at this position
  const cell = map.getCellAt(position);

  // skip if no base entity, if already visited, or if handling fails
  if (
    !cell?.base ||
    visited.includes(cell) ||
    !handler(cell.base.entity, cell.overlay?.entity)
  ) {
    return;
  }

  // add to visited
  visited.push(cell);

  // recurse left
  _blockIterateNeighborsR(
    map,
    handler,
    visited,
    new Vector2(position.x - 1, position.y),
  );
  // recurse right
  _blockIterateNeighborsR(
    map,
    handler,
    visited,
    new Vector2(position.x + 1, position.y),
  );
  // recurse up
  _blockIterateNeighborsR(
    map,
    handler,
    visited,
    new Vector2(position.x, position.y - 1),
  );
  // recurse down
  _blockIterateNeighborsR(
    map,
    handler,
    visited,
    new Vector2(position.x, position.y + 1),
  );
}

export function blockIterateNeighborsToGraph<T extends BlockEntity>(
  mapComponent: MapComponent,
  startEntity: T,
  predicate: BlockIterator,
): Graph<T> {
  const graph: Graph<T> = new Graph<T>((entity) => entity.name);

  // recurse on given block entity
  _blockIterateNeighborsToGraphR(
    mapComponent,
    predicate,
    startEntity.c.position.mapPosition,
    graph as any,
  );

  return graph;
}

function _blockIterateNeighborsToGraphR(
  mapComponent: MapComponent,
  predicate: BlockIterator,
  position: Vector2,
  graph: Graph<BlockEntity>,
  lastEntity?: BlockEntity,
) {
  // get block entity at this position
  const entity = mapComponent.getCellAt(position)?.base?.entity;

  // skip if no block entity or if predicate returns falsy
  if (!entity || !predicate(entity)) {
    return;
  }

  if (graph.hasNodeForObject(entity)) {
    // no need to iterate if already visited, but make sure nodes are connected
    if (lastEntity) {
      graph.addEdge(lastEntity, entity);
    }

    return;
  }

  // add to visited
  if (lastEntity) {
    graph.addEdge(lastEntity, entity);
  } else {
    graph.addVertex(entity);
  }

  // recurse left
  _blockIterateNeighborsToGraphR(
    mapComponent,
    predicate,
    new Vector2(position.x - 1, position.y),
    graph,
    entity,
  );
  // recurse right
  _blockIterateNeighborsToGraphR(
    mapComponent,
    predicate,
    new Vector2(position.x + 1, position.y),
    graph,
    entity,
  );
  // recurse up
  _blockIterateNeighborsToGraphR(
    mapComponent,
    predicate,
    new Vector2(position.x, position.y - 1),
    graph,
    entity,
  );
  // recurse down
  _blockIterateNeighborsToGraphR(
    mapComponent,
    predicate,
    new Vector2(position.x, position.y + 1),
    graph,
    entity,
  );
}

/**
 * Return just the 4 surrounding nearest neighbours, in the order top, right, bottom, left
 * If the handler predicate returns false, then that array element is undefined
 */
export function blockNearestNeighbors(
  mapComponent: MapComponent,
  startEntity: BlockEntity,
  handler: BlockIterator,
): BlockEntity[] | undefined {
  const position = startEntity.c.position.mapPosition;

  const topEntity = mapComponent.getCellAt(
    new Vector2(position.x, position.y - 1),
  )?.base?.entity;
  const rightEntity = mapComponent.getCellAt(
    new Vector2(position.x + 1, position.y),
  )?.base?.entity;
  const bottomEntity = mapComponent.getCellAt(
    new Vector2(position.x, position.y + 1),
  )?.base?.entity;
  const leftEntity = mapComponent.getCellAt(
    new Vector2(position.x - 1, position.y),
  )?.base?.entity;

  // Don't change the order!
  return [topEntity, rightEntity, bottomEntity, leftEntity].map(
    (entity: any) => {
      if (entity && handler(entity)) {
        return entity;
      }
      return undefined;
    },
  );
}

/**
 * Check if id represents a basic block
 * @param id
 * @returns
 */
export function blockIsBasic(id: BlockId): boolean {
  return blockPropsMap[id].type === 'basic';
}

/**
 * Check if id represents a powerup block
 * @param id
 * @returns
 */
export function blockIsPowerup(id: BlockId): boolean {
  return powerBlockTypes.includes(blockPropsMap[id].type as PowerBlockType);
}

/**
 * Check if id represents a special block
 * @param id
 * @returns
 */
export function blockIsSpecial(id: BlockId): boolean {
  return !blockIsBasic(id) && !blockIsPowerup(id);
}
